import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, RefreshCw, CheckCircle, XCircle, Mail, ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COMMON_PROVIDERS = [
  {
    name: "Gmail",
    smtp: { host: "smtp.gmail.com", port: 587 },
    imap: { host: "imap.gmail.com", port: 993 }
  },
  {
    name: "Outlook/Hotmail",
    smtp: { host: "smtp-mail.outlook.com", port: 587 },
    imap: { host: "outlook.office365.com", port: 993 }
  },
  {
    name: "Yahoo",
    smtp: { host: "smtp.mail.yahoo.com", port: 587 },
    imap: { host: "imap.mail.yahoo.com", port: 993 }
  }
];

export default function EmailConfiguration() {
  const navigate = useNavigate();
  const { configId } = useParams();
  const [loading, setLoading] = useState(false);
  const [testingImap, setTestingImap] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [imapStatus, setImapStatus] = useState(null);
  const [smtpStatus, setSmtpStatus] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    // IMAP Settings
    imap_host: "",
    imap_port: 993,
    imap_username: "",
    imap_password: "",
    imap_use_tls: true,
    // SMTP Settings
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    smtp_use_tls: true,
    // Auto Sync Settings
    auto_sync_enabled: false,
    sync_interval: 60,  // Default 1 hour
    last_sync: null
  });

  useEffect(() => {
    if (configId && configId !== "new") {
      fetchConfig();
    }
  }, [configId]);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/settings/communication/smtp`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const config = response.data.find(c => c.id === configId);
      if (config) {
        setFormData({
          name: config.name,
          email: config.email,
          imap_host: config.imap_host || "",
          imap_port: config.imap_port || 993,
          imap_username: config.imap_username || config.smtp_username,
          imap_password: config.imap_password || config.smtp_password,
          imap_use_tls: config.imap_use_tls !== undefined ? config.imap_use_tls : true,
          smtp_host: config.smtp_host,
          smtp_port: config.smtp_port,
          smtp_username: config.smtp_username,
          smtp_password: config.smtp_password,
          smtp_use_tls: config.smtp_use_tls !== undefined ? config.smtp_use_tls : true,
          auto_sync_enabled: config.auto_sync_enabled || false,
          sync_interval: config.sync_interval || 60,  // Default 1 hour
          last_sync: config.last_sync
        });
      }
    } catch (error) {
      toast.error("Failed to fetch configuration");
    }
  };

  const handleTestConnection = async (type) => {
    const isImap = type === "imap";
    const setter = isImap ? setTestingImap : setTestingSmtp;
    const statusSetter = isImap ? setImapStatus : setSmtpStatus;
    
    setter(true);
    statusSetter(null);

    try {
      const token = localStorage.getItem("token");
      const payload = {
        host: isImap ? formData.imap_host : formData.smtp_host,
        port: isImap ? formData.imap_port : formData.smtp_port,
        username: isImap ? formData.imap_username : formData.smtp_username,
        password: isImap ? formData.imap_password : formData.smtp_password,
        use_tls: isImap ? formData.imap_use_tls : formData.smtp_use_tls,
        connection_type: type
      };

      const response = await axios.post(`${API}/settings/communication/test-connection`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      statusSetter({ success: true, message: response.data.message });
      toast.success(response.data.message);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Connection failed";
      statusSetter({ success: false, message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setter(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email || !formData.smtp_host || !formData.smtp_username) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      if (configId && configId !== "new") {
        // Update existing
        await axios.put(`${API}/settings/communication/smtp/${configId}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Email configuration updated successfully");
      } else {
        // Create new
        await axios.post(`${API}/settings/communication/smtp`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Email configuration created successfully");
      }
      
      navigate("/settings/communication");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save configuration");
    } finally {
      setLoading(false);
    }
  };

  const applyProvider = (provider) => {
    setFormData({
      ...formData,
      smtp_host: provider.smtp.host,
      smtp_port: provider.smtp.port,
      imap_host: provider.imap.host,
      imap_port: provider.imap.port
    });
    toast.success(`${provider.name} settings applied`);
  };

  const getNextSyncTime = () => {
    if (!formData.auto_sync_enabled || !formData.last_sync) return "-";
    const lastSync = new Date(formData.last_sync);
    const nextSync = new Date(lastSync.getTime() + formData.sync_interval * 60000);
    const now = new Date();
    const diffMs = nextSync - now;
    
    if (diffMs < 0) return "Syncing now...";
    
    const mins = Math.floor(diffMs / 60000);
    return `${mins} min${mins !== 1 ? 's' : ''}`;
  };

  return (
    <div className="p-8" data-testid="email-configuration-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/settings/communication")}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">
              {configId && configId !== "new" ? "Edit" : "Add"} Email Configuration
            </h1>
            <p className="text-slate-600">Configure SMTP and IMAP settings for email integration</p>
          </div>
        </div>

        {/* Basic Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Display Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Ben Thomas"
                  className="h-11"
                />
              </div>
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="benison.thomas@shoppias.com"
                  className="h-11"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* IMAP and SMTP Settings */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* IMAP Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                IMAP Settings (Incoming)
              </CardTitle>
              <CardDescription>Configure incoming mail server</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="imap_host">IMAP Host *</Label>
                <Input
                  id="imap_host"
                  value={formData.imap_host}
                  onChange={(e) => setFormData({ ...formData, imap_host: e.target.value })}
                  placeholder="imap.gmail.com"
                  className="h-11"
                />
              </div>
              <div>
                <Label htmlFor="imap_port">IMAP Port *</Label>
                <Input
                  id="imap_port"
                  type="number"
                  value={formData.imap_port}
                  onChange={(e) => setFormData({ ...formData, imap_port: parseInt(e.target.value) })}
                  className="h-11"
                />
              </div>
              <div>
                <Label htmlFor="imap_username">Username *</Label>
                <Input
                  id="imap_username"
                  value={formData.imap_username}
                  onChange={(e) => setFormData({ ...formData, imap_username: e.target.value })}
                  placeholder="your-email@example.com"
                  className="h-11"
                />
              </div>
              <div>
                <Label htmlFor="imap_password">Password *</Label>
                <Input
                  id="imap_password"
                  type="password"
                  value={formData.imap_password}
                  onChange={(e) => setFormData({ ...formData, imap_password: e.target.value })}
                  className="h-11"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="imap_use_tls"
                  checked={formData.imap_use_tls}
                  onCheckedChange={(checked) => setFormData({ ...formData, imap_use_tls: checked })}
                />
                <Label htmlFor="imap_use_tls">Use SSL/TLS</Label>
              </div>
              
              <Button
                onClick={() => handleTestConnection("imap")}
                disabled={testingImap || !formData.imap_host}
                className="w-full"
                variant="outline"
              >
                {testingImap ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  "Test IMAP Connection"
                )}
              </Button>
              
              {imapStatus && (
                <div className={`flex items-center gap-2 p-3 rounded ${imapStatus.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  {imapStatus.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  <span className="text-sm">{imapStatus.message}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SMTP Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                SMTP Settings (Outgoing)
              </CardTitle>
              <CardDescription>Configure outgoing mail server</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="smtp_host">SMTP Host *</Label>
                <Input
                  id="smtp_host"
                  value={formData.smtp_host}
                  onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                  placeholder="smtp.gmail.com"
                  className="h-11"
                />
              </div>
              <div>
                <Label htmlFor="smtp_port">SMTP Port *</Label>
                <Input
                  id="smtp_port"
                  type="number"
                  value={formData.smtp_port}
                  onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) })}
                  className="h-11"
                />
              </div>
              <div>
                <Label htmlFor="smtp_username">Username *</Label>
                <Input
                  id="smtp_username"
                  value={formData.smtp_username}
                  onChange={(e) => setFormData({ ...formData, smtp_username: e.target.value })}
                  placeholder="your-email@example.com"
                  className="h-11"
                />
              </div>
              <div>
                <Label htmlFor="smtp_password">Password *</Label>
                <Input
                  id="smtp_password"
                  type="password"
                  value={formData.smtp_password}
                  onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                  className="h-11"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="smtp_use_tls"
                  checked={formData.smtp_use_tls}
                  onCheckedChange={(checked) => setFormData({ ...formData, smtp_use_tls: checked })}
                />
                <Label htmlFor="smtp_use_tls">Use SSL/TLS</Label>
              </div>
              
              <Button
                onClick={() => handleTestConnection("smtp")}
                disabled={testingSmtp || !formData.smtp_host}
                className="w-full"
                variant="outline"
              >
                {testingSmtp ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  "Test SMTP Connection"
                )}
              </Button>
              
              {smtpStatus && (
                <div className={`flex items-center gap-2 p-3 rounded ${smtpStatus.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  {smtpStatus.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  <span className="text-sm">{smtpStatus.message}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Auto Sync Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Auto Sync Settings</CardTitle>
            <CardDescription>Automatically sync emails at regular intervals</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Switch
                  id="auto_sync"
                  checked={formData.auto_sync_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, auto_sync_enabled: checked })}
                />
                <Label htmlFor="auto_sync" className="font-semibold">Enable Auto Sync</Label>
              </div>
              
              {formData.auto_sync_enabled && (
                <div className="flex items-center gap-4">
                  <div>
                    <Label className="text-xs text-slate-600">Sync Interval</Label>
                    <Select
                      value={formData.sync_interval.toString()}
                      onValueChange={(value) => setFormData({ ...formData, sync_interval: parseInt(value) })}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="60">Every 1 hour</SelectItem>
                        <SelectItem value="120">Every 2 hours</SelectItem>
                        <SelectItem value="180">Every 3 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-600">Last Sync</Label>
                    <div className="text-sm font-medium">
                      {formData.last_sync ? new Date(formData.last_sync).toLocaleString() : "Never"}
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-600">Next Sync In</Label>
                    <div className="text-sm font-medium">{getNextSyncTime()}</div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Common Providers */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Quick Setup - Common Email Providers</CardTitle>
            <CardDescription>Click to auto-fill settings for popular email providers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              {COMMON_PROVIDERS.map((provider) => (
                <Button
                  key={provider.name}
                  onClick={() => applyProvider(provider)}
                  variant="outline"
                  className="flex-1"
                >
                  {provider.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/settings/communication")}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>
    </div>
  );
}
